export interface LinkMatch {
    /** Index of the link's first character in the source text. */
    start: number;
    /** Index after the link's last character in the source text. */
    end: number;
    /** The link exactly as written, with trailing punctuation removed. */
    value: string;
    /** Absolute href, with a scheme added when the sender omitted one. */
    href: string;
}

export type TextSegment =
    | { type: 'text'; value: string }
    | { type: 'link'; value: string; href: string };

/**
 * Matches `http(s)://` and bare `www.` hosts. The body is greedy on purpose;
 * trailing sentence punctuation is stripped afterwards so a link at the end of
 * a sentence keeps its full path.
 */
const LINK_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

const TRAILING_PUNCTUATION = /[.,;:!?'"»”’]+$/u;

const BRACKET_PAIRS: Record<string, string> = {
    ')': '(',
    ']': '[',
    '}': '{',
};

function count(text: string, char: string): number {
    let total = 0;

    for (const character of text) {
        if (character === char) {
            total += 1;
        }
    }

    return total;
}

function trimTrailing(raw: string): string {
    let value = raw.replace(TRAILING_PUNCTUATION, '');

    // Drop a trailing bracket only when it closes nothing, so links that end
    // with a balanced `()`/`[]`/`{}` (common in Wikipedia-style URLs) survive.
    while (value.length > 0) {
        const last = value[value.length - 1];
        const opener = BRACKET_PAIRS[last];

        if (
            opener === undefined ||
            count(value, last) <= count(value, opener)
        ) {
            break;
        }

        value = value.slice(0, -1);
    }

    return value;
}

function hasHost(value: string): boolean {
    const host = value
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .split(/[/?#]/)[0];

    return host.includes('.') || /:\d+$/.test(host);
}

function toHref(value: string): string {
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

/**
 * Finds every link in a plain-text message, preserving its position so callers
 * can render it in place (and skip parsing that range for other formatting).
 */
export function findLinks(text: string): LinkMatch[] {
    const matches: LinkMatch[] = [];

    LINK_PATTERN.lastIndex = 0;

    let match = LINK_PATTERN.exec(text);

    while (match !== null) {
        const value = trimTrailing(match[0]);

        if (value !== '' && hasHost(value)) {
            matches.push({
                start: match.index,
                end: match.index + value.length,
                value,
                href: toHref(value),
            });
        }

        match = LINK_PATTERN.exec(text);
    }

    return matches;
}

/**
 * Splits plain text into alternating text and link segments. Used by surfaces
 * that render raw message text without WhatsApp formatting.
 */
export function splitLinks(text: string): TextSegment[] {
    const links = findLinks(text);

    if (links.length === 0) {
        return text === '' ? [] : [{ type: 'text', value: text }];
    }

    const segments: TextSegment[] = [];
    let cursor = 0;

    for (const link of links) {
        if (link.start > cursor) {
            segments.push({
                type: 'text',
                value: text.slice(cursor, link.start),
            });
        }

        segments.push({ type: 'link', value: link.value, href: link.href });
        cursor = link.end;
    }

    if (cursor < text.length) {
        segments.push({ type: 'text', value: text.slice(cursor) });
    }

    return segments;
}
